import { keepPreviousData, useQuery } from '@tanstack/react-query';
import type {
  ColumnSort,
  OnChangeFn,
  SortingState,
} from '@tanstack/react-table';
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Icon } from '~/components';
import { ErrorToast } from '~/components/ErrorToast/ErrorToast';
import { STICKY_TABLE_HEADER_CLASSES } from '~/components/ScrollCollapse/constants/classes';
import { DataUnavailable } from '~/modules/artist-roster/components/DataUnavailable/DataUnavailable';
import { PreReleaseTableBody } from '~/modules/artist-roster/components/PreReleaseTable/PreReleaseTableBody';
import {
  Table,
  TableHead,
  TableHeader,
  TableRow,
} from '~/modules/artist-roster/components/Table';
import { TableHeaderBackground } from '~/modules/artist-roster/components/Table/TableHeaderBackground';
import { useArtistTracksQuery } from '~/modules/artist-roster/hooks/useArtistTracksQuery';
import { usePreReleasesUrlParams } from '~/modules/artist-roster/hooks/usePreReleasesUrlParams/usePreReleasesUrlParams';
import { getBenchmarkTargets } from '~/modules/artist-roster/lib/trackTarget';
import type {
  ArtistDetails,
  AssetFamily,
} from '~/modules/artist-roster/lib/types';

import { usePreReleaseTableColumns } from './usePreReleaseTableColumns';

export interface PreReleaseTableMeta {
  artistName: string;
  filtersCount: number;
  filtersApplied: boolean;
  uaid: number | null;
  partyId: number;
}

interface PreReleaseTableProps {
  uaid: number | null;
  artistData: ArtistDetails;
  onSortingChange?: OnChangeFn<SortingState>;
  columnSort?: ColumnSort;
  handleSetBenchmarkButtonClick: () => void;
}

export function PreReleaseTable({
  uaid,
  artistData,
  columnSort,
  onSortingChange,
  handleSetBenchmarkButtonClick,
}: PreReleaseTableProps) {
  const { t } = useTranslation('roster');
  const [isErrorToastOpen, setIsErrorToastOpen] = useState(false);

  const { pageNumber, filters } = usePreReleasesUrlParams();
  const { releaseType, searchText } = filters;

  const {
    data: tracksData,
    error: tracksError,
    isLoading: isLoadingTracks,
    isFetching: isFetchingTracks,
  } = useArtistTracksQuery({
    uaid,
    columnSort,
    pageNumber,
    searchText,
    releaseType,
    isReleased: false,
  });

  const tracksDataItems = useMemo(() => tracksData?.items || [], [tracksData]);

  const familyIds = useMemo(
    () => [...new Set(tracksDataItems?.map((track) => track.id))].sort(),
    [tracksDataItems],
  );

  const {
    data: benchmarkData,
    isLoading: isLoadingBenchmark,
    error: benchmarkError,
  } = useQuery({
    queryKey: ['benchmark-tracks-performance', familyIds],
    queryFn: () => getBenchmarkTargets({ familyIds }),
    enabled: !isLoadingTracks && !!familyIds,
    placeholderData: keepPreviousData,
  });

  const trackAndBenchmarkData = useMemo<AssetFamily[]>(() => {
    if (benchmarkError) {
      setIsErrorToastOpen(true);
    }

    if (!benchmarkData) {
      return tracksDataItems;
    }

    return tracksDataItems.map((track) => {
      const benchmark = benchmarkData.find(
        (benchmarkTrack) =>
          benchmarkTrack.familyId.toString() === track.id.toString(),
      );

      if (!benchmark) {
        return track;
      }

      return {
        ...track,
        benchmark,
      };
    });
  }, [tracksDataItems, benchmarkData, benchmarkError]);

  const meta = {
    artistName: artistData.name,
    filtersCount: filters?.releaseType !== undefined ? 1 : 0,
    filtersApplied: filters !== undefined,
    partyId: artistData.partyId,
    uaid,
  };

  const columns = usePreReleaseTableColumns(
    handleSetBenchmarkButtonClick,
    artistData,
  );
  const table = useReactTable({
    data: trackAndBenchmarkData,
    columns,
    meta,
    getCoreRowModel: getCoreRowModel(),
    enableSortingRemoval: false,
    manualSorting: true,
    onSortingChange,
    state: {
      sorting: columnSort ? [columnSort] : undefined,
    },
  });

  if (tracksError) {
    return (
      <DataUnavailable
        title={t('errorData.title')}
        subtitle={t('errorData.sub')}
        icon={
          <Icon
            type="waveform"
            size="4x"
            variant="light"
            aria-hidden
            className="text-default"
          />
        }
      />
    );
  }

  return (
    <>
      <Table aria-label={t('prereleases.table.aria')}>
        <TableHeader className={STICKY_TABLE_HEADER_CLASSES}>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header, index) => {
                return (
                  <TableHead
                    key={header.id}
                    className="text-body-sm font-normal first:w-1/3 first:pl-lg"
                  >
                    {!index ? <TableHeaderBackground /> : null}
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                  </TableHead>
                );
              })}
            </TableRow>
          ))}
        </TableHeader>
        <PreReleaseTableBody
          table={table}
          meta={meta}
          artistData={artistData}
          isFetchingTracks={isFetchingTracks}
          isLoadingBenchmark={isLoadingBenchmark}
        />
      </Table>
      <ErrorToast
        isOpen={isErrorToastOpen}
        onClose={() => setIsErrorToastOpen(false)}
        title={t('benchmarks.error.title')}
        description={t('benchmarks.error.caption')}
        className="z-50 w-[333px] max-w-[333px]"
      />
    </>
  );
}

PreReleaseTable.displayName = 'PreReleaseTable';
